import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { toast } from "sonner";
import { z } from "zod";

import { useIsWondaCliPlansEnabled } from "@/hooks/useIsWondaCliPlansEnabled";

const PROVIDER_DISPLAY_NAME: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  meta: "Meta Ads",
  twitter: "X",
  linkedin: "LinkedIn",
  reddit: "Reddit",
};

const searchSchema = z.object({
  oauth_provider: z.string().optional(),
  oauth_error: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/(wonda)/_wonda/account/")(
  {
    validateSearch: zodValidator(searchSchema),
    component: AccountIndex,
  },
);

function AccountIndex() {
  const { oauth_provider, oauth_error } = Route.useSearch();
  const isPlansEnabled = useIsWondaCliPlansEnabled();
  const navigate = useNavigate();
  const hasNavigated = React.useRef(false);

  React.useEffect(() => {
    if (hasNavigated.current) return;

    // OAuth redirects always go to connected-accounts — no need to wait for flags
    if (oauth_provider) {
      hasNavigated.current = true;
      const name = PROVIDER_DISPLAY_NAME[oauth_provider] ?? oauth_provider;
      if (oauth_error) {
        toast.error(`${name} connection failed: ${oauth_error}`);
      } else {
        toast.success(`${name} account connected`);
      }
      void navigate({ to: "/account/accounts", replace: true });
      return;
    }

    // Wait for PostHog to resolve the flag before choosing default tab
    if (isPlansEnabled === undefined) return;

    hasNavigated.current = true;
    void navigate({
      to: isPlansEnabled ? "/account/plan" : "/account/accounts",
      replace: true,
    });
  }, [oauth_provider, oauth_error, isPlansEnabled, navigate]);

  return null;
}
